import { useState, useEffect, useCallback } from "react";
import { Loader, Select, SelectOption, SelectProps } from "rizzui";
import UserRepository from "@/utilities/repositories/Users";
import { IUserData } from "@/utilities/types/Users";
import { debounce } from "lodash";
import cn from "@/utils/class-names";

export interface UsersFilterProps
  extends Omit<SelectProps<SelectOption>, "options"> {
  onChange: (selectedUser: IUserData | null) => void;
  placeholder?: string;
  className?: string;
  label?: string;
}

export default function UsersFilter({
  onChange,
  placeholder = "Select user",
  className,
  label,
  ...props
}: Readonly<UsersFilterProps>) {
  const [users, setUsers] = useState<IUserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);

  const [searchQuery, setSearchQuery] = useState("");

  // Transform users data into Select options format
  const transformUsersToOptions = (users: IUserData[]): SelectOption[] => {
    return users.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName} (${user.email})`,
      data: user,
    }));
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await UserRepository.getUsers({
        queryParams: {
          pagination: {
            limit: 200,
            page: 1,
          },
          query: {
            search: {
              search: searchQuery,
            },
            filter: [],
            sort: {
              createdAt: "DESC",
            },
          },
        },
      });
      setUsers(response.data.items);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Initialize users
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSelectChange = (option: SelectOption | null) => {
    setSelectedUser(option);
    onChange?.(option?.data || null);
  };

  const handleClear = () => {
    handleSelectChange(null);
  };

  useEffect(() => {
    if (!props.value) {
      setSelectedUser(null);
    } else {
      // Find the user in the users array that matches the value
      const matchedUser = users.find((user) => user.id === props.value);
      if (matchedUser) {
        const selectedOption = transformUsersToOptions([matchedUser])[0];
        setSelectedUser(selectedOption);
      }
    }
  }, [props?.value, users]);

  return (
    <div className={cn("w-[250px]", className)}>
      <Select
        label={label}
        searchable
        clearable
        placeholder={placeholder}
        searchPlaceHolder="Search by name"
        searchSuffix={loading ? <Loader size="sm" /> : undefined}
        {...props}
        onSearchChange={debounce(setSearchQuery, 500)}
        disableDefaultFilter
        searchClassName="sticky"
        options={transformUsersToOptions(users)}
        value={selectedUser}
        onChange={handleSelectChange}
        onClear={() => {
          handleClear();
        }}
      />
    </div>
  );
}
